import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import PopoverHeader from "@/components/ui/popover-header";
import { Button } from "@/components/ui/button";
import { formatFullDate, toCalendarDate } from "@/utils/datetime";
import { UserData } from "@/entities/users/types";
import { isEmpty } from "lodash";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editText?: string;
  statusReasonTitle?: string | null;
  statusReasonText?: string | null;
  statusNotesTitle?: string | null;
  statusNotes?: string | null;
  statusChangedAt?: string | null;
  changedByUser?: UserData;
  onEdit?: () => void;
  dreamerBadge?: React.ReactNode;
  setByLabel?: string | null;
}

const DetailsPopover: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  editText,
  statusReasonTitle,
  statusReasonText,
  statusNotesTitle,
  statusNotes,
  statusChangedAt,
  changedByUser,
  onEdit,
  dreamerBadge,
  setByLabel
}) => {
  const { t } = useTranslation();
  const statusDate = statusChangedAt
    ? formatFullDate(toCalendarDate(statusChangedAt))
    : "";

  return (
    <Popover open={isOpen} onOpenChange={(open: boolean) => setIsOpen(open)}>
      <PopoverTrigger>
        <Text variant="smBody" className="text-link">
          {t("details")}
        </Text>
      </PopoverTrigger>
      <PopoverContent
        padding={0}
        className="flex flex-col max-w-[45rem]"
        align="start"
        side="bottom"
        onEscapeKeyDown={() => setIsOpen(false)}
        sideOffset={4}
      >
        <PopoverHeader
          title={t("details")}
          onClose={() => setIsOpen(false)}
          actions={
            <Button variant="tertiary" onClick={onEdit}>
              {editText ? editText : t("people.personView.edit")}
            </Button>
          }
        />
        <div className="p-4 flex flex-col gap-4">
          {(statusReasonTitle || statusReasonText) && (
            <div className="flex flex-col gap-2">
              {!isEmpty(statusReasonTitle) && (
                <div className="flex items-center gap-2">
                  <Text variant="bodySecondary">{statusReasonTitle}</Text>
                  {dreamerBadge}
                </div>
              )}
              {!isEmpty(statusReasonText) && (
                <Text variant="body">{statusReasonText}</Text>
              )}
            </div>
          )}
          {statusNotes && (
            <div className="flex flex-col gap-2">
              {!isEmpty(statusNotesTitle) && (
                <Text variant="bodySecondary">{statusNotesTitle}</Text>
              )}
              {!isEmpty(statusNotes) && (
                <Text variant="body">{statusNotes}</Text>
              )}
            </div>
          )}
          {!isEmpty(statusChangedAt) && (setByLabel || !isEmpty(changedByUser)) && (
            <Text variant="smBodySecondary">
              {setByLabel
                ? `${setByLabel} ${t("people.personView.on")} ${statusDate}`
                : `${t("people.personView.setBy")} ${changedByUser?.displayName} ${t("people.personView.on")} ${statusDate}`}
            </Text>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DetailsPopover;
